// Auth Context - Manages authentication state following Kratos browser flow patterns
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { Session } from '@ory/client';
import { AxiosError } from 'axios';
import { getOrySdk } from '../services/ory';
import { getUserFromSession } from '../services/auth';

interface User {
  id: string;
  email: string;
  name: string;
  verified: boolean;
}

interface AuthContextValue {
  // State
  session: Session | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  didFetch: boolean;

  // Actions
  syncSession: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [didFetch, setDidFetch] = useState(false);

  // Derive user from session
  const user = getUserFromSession(session);
  const isAuthenticated = !!session;

  // Sync session - validates session cookie with backend
  const syncSession = useCallback(async () => {
    setIsLoading(true);
    try {
      const sdk = getOrySdk();
      // toSession() uses cookies to validate the session
      const response = await sdk.toSession();
      setSession(response.data);
    } catch (error) {
      const axiosErr = error as AxiosError;
      // 401 means no valid session
      if (axiosErr.response?.status === 401) {
        setSession(null);
      } else if (axiosErr.response?.status === 422) {
        // Session requires additional authentication (e.g., 2FA)
        // Keep session null, let the UI handle the redirect
        setSession(null);
      } else {
        console.error('Session sync error:', error);
        setSession(null);
      }
    } finally {
      setIsLoading(false);
      setDidFetch(true);
    }
  }, []);

  // Logout - following the reference pattern
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      const sdk = getOrySdk();
      // First, create a logout flow to get the logout token
      const { data: logoutFlow } = await sdk.createBrowserLogoutFlow();

      // Then perform the logout
      if (logoutFlow.logout_token) {
        await sdk.updateLogoutFlow({ token: logoutFlow.logout_token });
      }
    } catch (error) {
      const axiosErr = error as AxiosError;
      // 401 means already logged out - that's fine
      if (axiosErr.response?.status !== 401) {
        console.log('Logout error (ignored):', error);
      }
    } finally {
      // Clear local state regardless
      setSession(null);
      setIsLoading(false);
    }
  }, []);

  // Initialize - check for existing session on mount
  useEffect(() => {
    syncSession();
  }, [syncSession]);

  // Refresh session when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && didFetch) {
        syncSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [didFetch, syncSession]);

  const value: AuthContextValue = {
    session,
    user,
    isAuthenticated,
    isLoading,
    didFetch,
    syncSession,
    logout,
  };

  // Don't render children until initial auth check completes
  if (!didFetch) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hook to require authentication
export const useRequireAuth = (): AuthContextValue => {
  const auth = useAuth();
  if (!auth.isAuthenticated && auth.didFetch) {
    throw new Error('User must be authenticated');
  }
  return auth;
};
